import React, { useContext, useEffect, useState } from 'react'
import moment from 'moment'
import API from '../../../../services/API'

import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Chip from '@mui/material/Chip'
import LinearProgress from '@mui/material/LinearProgress'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import VisibilityIcon from '@mui/icons-material/Visibility'
import { DataGrid } from '@mui/x-data-grid'
import { PayrollContext } from './Payroll'
import { TenantSettingsContext } from '../../../../App'
import { formatMoney } from '../../../../helpers/currency'
import useDocumentPreview from '../../../../components/Documents/useDocumentPreview'

const employeeName = (expense) =>
  `${expense?.employee?.userAccount?.firstName ?? ''} ${
    expense?.employee?.userAccount?.lastName ?? ''
  }`.trim() || 'Unknown employee'

const UnpaidExpenses = ({ refreshKey }) => {
  const { selectedExpenses, setSelectedExpenses } = useContext(PayrollContext)
  const { orgSettings } = useContext(TenantSettingsContext).orgConfig
  const [expenses, setExpenses] = useState([])
  const [loading, setLoading] = useState(false)
  const { openPreview, previewModal } = useDocumentPreview()

  const currency = (value) => formatMoney(value, orgSettings?.currency)

  const loadExpenses = () => {
    setLoading(true)
    // The server filters to approved, unpaid, not-in-a-run expenses and
    // returns them uncapped - no client-side filtering on top
    API.get('/payroll/expenses', { params: { unpaid: true } })
      .then((res) => {
        if (res.status === 200) {
          setExpenses(res.data)
        }
      })
      .finally(() => setLoading(false))
  }

  useEffect(() => {
    loadExpenses()
  }, [refreshKey])

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack gap={2}>
        <Stack>
          <Typography variant="title2">Unpaid Expenses</Typography>
          <Typography variant="body2" color="text.secondary">
            Approved expenses awaiting a pay run. Select rows here alongside shifts
            below to include them in the same pay run.
          </Typography>
        </Stack>
        {loading && <LinearProgress />}
        {!loading && expenses.length === 0 ? (
          <Alert severity="info" variant="outlined">
            No approved expenses are waiting to be paid.
          </Alert>
        ) : (
          <Box sx={{ height: 260 }}>
            <DataGrid
              rows={expenses}
              getRowId={(row) => row._id}
              checkboxSelection
              disableRowSelectionOnClick
              rowSelectionModel={selectedExpenses}
              onRowSelectionModelChange={(selection) =>
                setSelectedExpenses(Array.from(selection))
              }
              columns={[
                {
                  field: 'employee',
                  headerName: 'Employee',
                  minWidth: 180,
                  flex: 1,
                  valueGetter: (value, row) => employeeName(row),
                },
                {
                  field: 'description',
                  headerName: 'Description',
                  minWidth: 190,
                  flex: 1,
                },
                {
                  field: 'category',
                  headerName: 'Category',
                  minWidth: 130,
                },
                {
                  field: 'expenseDate',
                  headerName: 'Date',
                  minWidth: 130,
                  valueGetter: (value) =>
                    value ? moment(value).format('DD MMM YYYY') : '',
                },
                {
                  field: 'amount',
                  headerName: 'Amount',
                  minWidth: 120,
                  renderCell: ({ value }) => (
                    <Chip size="small" label={currency(value)} sx={{ borderRadius: 1 }} />
                  ),
                },
                {
                  field: 'receipt',
                  headerName: '',
                  minWidth: 120,
                  sortable: false,
                  renderCell: ({ row }) => (
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon />}
                      onClick={() => openPreview(row.receipt)}
                      disabled={!row.receipt?.key}
                    >
                      View
                    </Button>
                  ),
                },
              ]}
            />
          </Box>
        )}
      </Stack>
      {previewModal}
    </Paper>
  )
}

export default UnpaidExpenses
